<script setup lang="ts">
/**
 * Inline editor for a custom highlight — used for BOTH creating a new item and editing an
 * existing one (the parent decides which by seeding `initialText`).
 *
 * Styled to the Figma RAW redesign (node 1791:99905): sits on a raised surface, shows the
 * item's index number (no drag grip while editing), an auto-growing textarea with NO max
 * height (grows with content) and a clear affordance, and larger Cancel / Save buttons in a
 * footer row for accessibility.
 *
 * This is a purely PRESENTATIONAL component: it owns its own `draft` text
 * and the trim / whitespace-only validation, because "what is typed in the open textarea and
 * whether it may be committed" is a UI concern, not a data-model one. The headless
 * `useCustomList` composable only ever receives an already-valid, trimmed name via the
 * `save` event.
 */
import { ref, computed, nextTick, onMounted } from "vue";
import NovaTextarea from "@/ui-kit/NovaTextarea/NovaTextarea.vue";
import NovaButton from "@/ui-kit/NovaButton/NovaButton.vue";

const props = withDefaults(
  defineProps<{
    /** Seed text — the current name when editing, empty when creating. */
    initialText?: string;
    /** 1-based number shown in the index badge (matches the read-only row). */
    index?: number;
  }>(),
  { initialText: "", index: undefined }
);

const emit = defineEmits<{
  /** Commit a trimmed, non-empty name. */
  (e: "save", name: string): void;
  /** Abandon without committing. */
  (e: "cancel"): void;
}>();

const draft = ref(props.initialText);

/** Non-empty after trimming — gates the Save button. */
const canSave = computed(() => draft.value.trim().length > 0);

// Focus the textarea when the editor opens. NovaTextarea auto-resizes via `autoresize` so it
// grows with content and has no max height (Figma requirement).
const editorEl = ref<HTMLElement | null>(null);
onMounted(() => {
  nextTick(() => {
    editorEl.value?.querySelector("textarea")?.focus();
  });
});

function save(): void {
  const name = draft.value.trim();
  if (name.length === 0) {
    return;
  }
  emit("save", name);
}

function cancel(): void {
  emit("cancel");
}
</script>

<template>
  <div
    ref="editorEl"
    class="flex w-full flex-col gap-2 rounded-lg border border-neutral-60 bg-neutral-30 p-2"
    data-testid="highlight-item-editor"
  >
    <div class="flex items-start gap-2">
      <span
        v-if="index != null"
        class="mt-0.5 inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-secondary-20 px-1 text-center font-bold text-[12px] leading-4 text-secondary-80"
        data-testid="highlight-editor-index"
      >
        {{ index }}
      </span>

      <NovaTextarea
        id="highlight-editor"
        v-model="draft"
        class="flex-1"
        size="sm"
        :rows="1"
        autoresize
        data-testid="highlight-editor-input"
      />
    </div>

    <div class="flex items-center justify-end gap-2">
      <!-- Compact Cancel/Save per Figma (h-24, px-12 py-4, 12px label, radius 8). -->
      <NovaButton
        variant="outline"
        size="xs"
        class="h-6 rounded-[8px]! px-3 py-1"
        data-testid="highlight-editor-cancel"
        @click="cancel"
      >
        {{ $t("common.cancel") }}
      </NovaButton>
      <NovaButton
        variant="contained"
        size="xs"
        class="h-6 rounded-[8px]! px-3 py-1"
        :disabled="!canSave"
        data-testid="highlight-editor-save"
        @click="save"
      >
        {{ $t("common.save") }}
      </NovaButton>
    </div>
  </div>
</template>
